import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Button, Switch, Select, DatePicker, InputNumber, Card, message, Row, Col, Space, Typography, Divider } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { all_routes } from '../../../../router/all_routes';
import referralCampaignService from '../../../../services/super-admin/referral-campaign.service';
import { ReferralCampaign, CodeType, RewardType, CreateCampaignRequest } from '../../../../types/referral.types';
import moment from 'moment';
import { GiftOutlined, CloseOutlined, SaveOutlined, TrophyOutlined, FireOutlined } from '@ant-design/icons';
import FooterSection from '../../../shared/components/footerSection';

const { Title, Text } = Typography;

const { Option } = Select;
const { TextArea } = Input;

const CampaignForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  // v2.0: codeType state kaldırıldı - artık her zaman şirket bazlı kalıcı kodlar kullanılıyor
  const [newCompanyRewardType, setNewCompanyRewardType] = useState<RewardType>(RewardType.PERCENTAGE_DISCOUNT);
  const [referringCompanyRewardType, setReferringCompanyRewardType] = useState<RewardType>(RewardType.PERCENTAGE_DISCOUNT);

  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode) {
      fetchCampaign();
    }
  }, [id, isEditMode]);

  useEffect(() => {
    // v2.0: codeType kontrolü kaldırıldı
    const currentNewCompanyRewardType = form.getFieldValue('newCompanyRewardType');
    if (currentNewCompanyRewardType) {
      setNewCompanyRewardType(currentNewCompanyRewardType);
    }
    const currentReferringCompanyRewardType = form.getFieldValue('referringCompanyRewardType');
    if (currentReferringCompanyRewardType) {
      setReferringCompanyRewardType(currentReferringCompanyRewardType);
    }
  }, [form]);

  const getRewardValuePlaceholder = (rewardType: RewardType): string => {
    switch (rewardType) {
      case RewardType.PERCENTAGE_DISCOUNT:
        return 'Örn: 10 %';
      case RewardType.FIXED_AMOUNT_DISCOUNT:
        return 'Örn: 100 TL';
      case RewardType.FREE_SUBSCRIPTION_DAYS:
        return 'Örn: 30 gün';
      default:
        return 'Değer girin';
    }
  };

  // Sadece sayısal karakterlere izin veren fonksiyon (harf girişini engeller)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Kontrol tuşlarına izin ver (Backspace, Delete, Arrow keys, Tab, Enter, vb.)
    if (e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Tab' || e.key === 'Enter' ||
        e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
        e.key === 'Home' || e.key === 'End' || e.ctrlKey || e.metaKey) {
      return;
    }
    
    // Sadece sayılar (0-9) ve nokta (.) karakterine izin ver
    if (!/[0-9.]/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Yapıştırma işleminde sadece sayısal karakterleri kabul eden fonksiyon
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    // Sadece sayılar ve nokta karakterlerini kontrol et
    const numericOnly = pastedText.replace(/[^0-9.]/g, '');
    // Eğer yapıştırılan metin harf içeriyorsa, işlemi engelle
    if (pastedText !== numericOnly) {
      e.preventDefault();
      // Sadece sayısal kısmı yapıştır
      if (numericOnly) {
        const input = e.currentTarget as HTMLInputElement;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const currentValue = input.value || '';
        const newValue = currentValue.substring(0, start) + numericOnly + currentValue.substring(end);
        // InputNumber'ın onChange event'ini tetiklemek için form değerini güncelle
        const fieldName = input.getAttribute('name') || input.getAttribute('id');
        if (fieldName && form) {
          const numericValue = parseFloat(newValue);
          if (!isNaN(numericValue)) {
            form.setFieldValue(fieldName.replace('_input', ''), numericValue);
          }
        }
      }
    }
  };

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const campaign = await referralCampaignService.getCampaignById(id!);
      form.setFieldsValue({
        ...campaign,
        expiresAt: campaign.expiresAt ? moment(campaign.expiresAt) : undefined,
      });
      // v2.0: setCodeType kaldırıldı - artık her zaman şirket bazlı kalıcı kodlar kullanılıyor
      setNewCompanyRewardType(campaign.newCompanyRewardType);
      setReferringCompanyRewardType(campaign.referringCompanyRewardType);
    } catch (error: any) {
      message.error('Kampanya yüklenirken bir hata oluştu.');
      navigate(all_routes.superAdminReferralCampaigns);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const formData: CreateCampaignRequest = {
        name: values.name,
        description: values.description,
        isActive: values.isActive ?? true,
        codeType: CodeType.SINGLE_USE_BY_COMPANY, // v2.0: Her zaman şirket bazlı kalıcı kodlar (UI'da seçim yok)
        generalCode: undefined, // v2.0: Genel kod kullanılmıyor
        usageLimit: undefined, // v2.0: Kullanım limiti yok
        newCompanyRewardType: values.newCompanyRewardType,
        newCompanyRewardValue: values.newCompanyRewardValue,
        referringCompanyRewardType: values.referringCompanyRewardType,
        referringCompanyRewardValue: values.referringCompanyRewardValue,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : undefined,
      };

      if (isEditMode) {
        await referralCampaignService.updateCampaign({ id: id!, ...formData });
        message.success('Kampanya başarıyla güncellendi.');
      } else {
        await referralCampaignService.createCampaign(formData);
        message.success('Kampanya başarıyla oluşturuldu.');
      }

      navigate(all_routes.superAdminReferralCampaigns);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || 'Kampanya kaydedilirken bir hata oluştu.';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
            <div className="my-auto mb-2">
              <h2 className="mb-1" style={{ color: '#7338a1', fontWeight: 'bold' }}>
                <GiftOutlined style={{ color: '#7338a1', marginRight: '8px' }} />
                {isEditMode ? 'Kampanya Düzenle' : 'Yeni Kampanya Oluştur'}
              </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminDashboard2}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminReferralCampaigns}>Referans Kampanyaları</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEditMode ? 'Düzenle' : 'Yeni Kampanya'}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons ms-2">
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => navigate(all_routes.superAdminReferralCampaigns)}
                className="me-2"
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: 500
                }}
              >
                İptal
              </Button>
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Hero Section */}
          <Card
            style={{
              background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 50%, #c77dff 100%)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              border: 'none',
              boxShadow: '0 8px 24px rgba(115, 56, 161, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Dekoratif arka plan */}
            <div style={{
              position: 'absolute',
              top: '-60px',
              right: '-60px',
              width: '200px',
              height: '200px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            
            <div style={{ position: 'relative', zIndex: 1, color: 'white' }}>
              <div style={{
                display: 'inline-block',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                marginBottom: '16px',
                backdropFilter: 'blur(10px)'
              }}>
                <GiftOutlined style={{ fontSize: '32px', color: 'white' }} />
              </div>
              <Title level={3} style={{ color: 'white', marginBottom: '8px', fontWeight: 'bold' }}>
                {isEditMode ? 'Kampanya Bilgilerini Düzenle' : 'Yeni Kampanya Oluştur'}
              </Title>
              <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
                {isEditMode 
                  ? 'Kampanya bilgilerini güncelleyin ve değişiklikleri kaydedin.'
                  : 'Yeni bir referans kampanyası oluşturun ve ödül sistemini yapılandırın.'}
              </Text>
            </div>
          </Card>

          <Card
            style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 4px 16px rgba(115, 56, 161, 0.1)',
            }}
          >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true,
            codeType: CodeType.SINGLE_USE_BY_COMPANY, // v2.0: Backend için varsayılan değer (UI'da gösterilmiyor)
            newCompanyRewardType: RewardType.PERCENTAGE_DISCOUNT,
            referringCompanyRewardType: RewardType.PERCENTAGE_DISCOUNT,
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Kampanya Adı"
                rules={[{ required: true, message: 'Kampanya adı zorunludur' }]}
              >
                <Input placeholder="Kampanya adını girin" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="isActive"
                label="Durum"
                valuePropName="checked"
              >
                <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Açıklama"
          >
            <TextArea rows={3} placeholder="Kampanya açıklamasını girin" />
          </Form.Item>

          {/* v2.0: Kod türü seçimi kaldırıldı - artık her şirket için kalıcı, çok kullanımlı kodlar var */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="expiresAt"
                label="Bitiş Tarihi (Opsiyonel)"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  size="large"
                  showTime
                  format="DD/MM/YYYY HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* v2.0: Genel kod alanları kaldırıldı - artık her şirket kendi kalıcı kodunu oluşturuyor */}
          {false && (
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="generalCode"
                  label="Genel Kod"
                  rules={[{ required: true, message: 'Genel kod zorunludur' }]}
                >
                  <Input placeholder="Örn: FUAR2024" size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="usageLimit"
                  label="Kullanım Limiti (Opsiyonel)"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    size="large"
                    min={1}
                    placeholder="Sınırsız için boş bırakın"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TrophyOutlined style={{ color: '#7338a1' }} />
                <span style={{ fontWeight: 600, color: '#262626' }}>Yeni Şirket Ödülü</span>
              </div>
            }
            style={{
              marginTop: '24px',
              marginBottom: '24px',
              borderRadius: '12px',
              border: '1px solid #f0f0f0',
              background: 'linear-gradient(135deg, rgba(115, 56, 161, 0.02) 0%, rgba(157, 78, 221, 0.01) 100%)'
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="newCompanyRewardType"
                  label={<span style={{ fontWeight: 500 }}>Ödül Türü</span>}
                  rules={[{ required: true, message: 'Ödül türü zorunludur' }]}
                >
                  <Select 
                    size="large"
                    onChange={(value) => setNewCompanyRewardType(value)}
                    style={{ borderRadius: '8px' }}
                  >
                    <Option value={RewardType.PERCENTAGE_DISCOUNT}>Yüzde İndirim</Option>
                    <Option value={RewardType.FIXED_AMOUNT_DISCOUNT}>Sabit Tutar İndirimi</Option>
                    <Option value={RewardType.FREE_SUBSCRIPTION_DAYS}>Ücretsiz Abonelik Günü</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="newCompanyRewardValue"
                  label={<span style={{ fontWeight: 500 }}>Ödül Değeri</span>}
                  rules={[
                    { required: true, message: 'Ödül değeri zorunludur' },
                    { 
                      validator: (_, value) => {
                        if (value === null || value === undefined || value === '') {
                          return Promise.reject(new Error('Ödül değeri zorunludur'));
                        }
                        if (typeof value !== 'number' && isNaN(Number(value))) {
                          return Promise.reject(new Error('Sadece sayı girebilirsiniz'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%', borderRadius: '8px' }}
                    size="large"
                    min={0}
                    step={newCompanyRewardType === RewardType.PERCENTAGE_DISCOUNT ? 1 : newCompanyRewardType === RewardType.FREE_SUBSCRIPTION_DAYS ? 1 : 0.01}
                    precision={newCompanyRewardType === RewardType.PERCENTAGE_DISCOUNT ? 0 : newCompanyRewardType === RewardType.FREE_SUBSCRIPTION_DAYS ? 0 : 2}
                    placeholder={getRewardValuePlaceholder(newCompanyRewardType)}
                    controls={true}
                    onKeyPress={handleKeyPress}
                    onPaste={handlePaste}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FireOutlined style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: 600, color: '#262626' }}>Referans Eden Şirket Ödülü</span>
              </div>
            }
            style={{
              marginBottom: '24px',
              borderRadius: '12px',
              border: '1px solid #f0f0f0',
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.02) 0%, rgba(251, 191, 36, 0.01) 100%)'
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="referringCompanyRewardType"
                  label={<span style={{ fontWeight: 500 }}>Ödül Türü</span>}
                  rules={[{ required: true, message: 'Ödül türü zorunludur' }]}
                >
                  <Select 
                    size="large"
                    onChange={(value) => setReferringCompanyRewardType(value)}
                    style={{ borderRadius: '8px' }}
                  >
                    <Option value={RewardType.PERCENTAGE_DISCOUNT}>Yüzde İndirim</Option>
                    <Option value={RewardType.FIXED_AMOUNT_DISCOUNT}>Sabit Tutar İndirimi</Option>
                    <Option value={RewardType.FREE_SUBSCRIPTION_DAYS}>Ücretsiz Abonelik Günü</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="referringCompanyRewardValue"
                  label={<span style={{ fontWeight: 500 }}>Ödül Değeri</span>}
                  rules={[
                    { required: true, message: 'Ödül değeri zorunludur' },
                    { 
                      validator: (_, value) => {
                        if (value === null || value === undefined || value === '') {
                          return Promise.reject(new Error('Ödül değeri zorunludur'));
                        }
                        if (typeof value !== 'number' && isNaN(Number(value))) {
                          return Promise.reject(new Error('Sadece sayı girebilirsiniz'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%', borderRadius: '8px' }}
                    size="large"
                    min={0}
                    step={referringCompanyRewardType === RewardType.PERCENTAGE_DISCOUNT ? 1 : referringCompanyRewardType === RewardType.FREE_SUBSCRIPTION_DAYS ? 1 : 0.01}
                    precision={referringCompanyRewardType === RewardType.PERCENTAGE_DISCOUNT ? 0 : referringCompanyRewardType === RewardType.FREE_SUBSCRIPTION_DAYS ? 0 : 2}
                    placeholder={getRewardValuePlaceholder(referringCompanyRewardType)}
                    controls={true}
                    onKeyPress={handleKeyPress}
                    onPaste={handlePaste}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

            <Divider />
            <div className="form-actions" style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button
                danger
                onClick={() => navigate(all_routes.superAdminReferralCampaigns)}
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: 500,
                  height: '48px',
                  paddingLeft: '24px',
                  paddingRight: '24px'
                }}
              >
                İptal
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 100%)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(115, 56, 161, 0.3)',
                  paddingLeft: '32px',
                  paddingRight: '32px'
                }}
              >
                {isEditMode ? 'Güncelle' : 'Oluştur'}
              </Button>
            </div>
          </Form>
          </Card>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default CampaignForm;

